import React, { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'

const formatMoney = (amount, currency = 'GBP', locale = 'en-GB') => {
  const value = (amount ?? 0) / 100
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * BillingProrationSummary
 * Drop-in component to place inside your existing modal.
 *
 * Props:
 * - currency: 'GBP' | 'USD' | etc (default 'GBP')
 * - prorationDueNow: number (in minor units e.g. pence)
 * - newMonthly: number (in minor units e.g. pence)
 * - lines?: [{ desc, amount, proration }] (optional) for breakdown
 * - onConfirm?: () => void
 * - onCancel?: () => void
 * - confirmLabel?: string
 * - cancelLabel?: string
 * - loading?: boolean
 */
export default function BillingProrationSummary({
  currency = 'GBP',
  prorationDueNow = 0,
  newMonthly = 0,
  lines = [],
  onConfirm,
  onCancel,
  confirmLabel = 'Confirm update',
  cancelLabel = 'Not now',
  loading = false,
  interval = 'monthly',
  recurringLabel = 'New monthly total',
  recurringCaption = 'This will be your recurring total going forward.',
}) {
  const theme = useTheme()
  const [showBreakdown, setShowBreakdown] = useState(false)

  const accent =
    theme.palette?.anPurple?.main ?? theme.palette?.primary?.main ?? '#7b2cbf'

  const isCredit = prorationDueNow < 0

  const dueCopy =
    prorationDueNow > 0
      ? 'This amount will be charged to your saved payment method today.'
      : prorationDueNow < 0
        ? 'This change creates a credit that will be applied to your next invoice.'
        : 'No charge today — this change will apply from your next invoice.'

  const breakdown = useMemo(() => {
    if (!lines?.length) return null

    const now = lines.filter((l) => l.proration)
    const recurring = lines.filter((l) => !l.proration)

    return {
      now,
      recurring,
      totalNow: now.reduce((s, l) => s + (l.amount ?? 0), 0),
      totalRecurring: recurring.reduce((s, l) => s + (l.amount ?? 0), 0),
    }
  }, [lines])

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* subtle header strip */}
      <Box sx={{ height: 3, bgcolor: accent }} />

      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2}>
          {/* Title + hint */}
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <ReceiptLongRoundedIcon fontSize="small" />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={950} sx={{ lineHeight: 1.15 }}>
                Review billing change
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Stripe will apply a prorated adjustment for the remainder of
                your current billing period.
              </Typography>
            </Box>

            <Chip
              size="small"
              label={isCredit ? 'Credit' : 'Prorated'}
              sx={{
                height: 26,
                borderRadius: 2,
                fontWeight: 900,
                bgcolor: 'action.hover',
              }}
            />
          </Stack>

          {/* Summary cards */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.25}
            sx={{ mt: 0.5 }}
          >
            {/* Due now */}
            <Box
              sx={{
                flex: 1,
                p: 1.75,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <BoltRoundedIcon fontSize="small" />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={800}
                  >
                    Due today (estimate)
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 950,
                      lineHeight: 1.1,
                      color: isCredit
                        ? theme.palette.success.main
                        : 'text.primary',
                    }}
                  >
                    {formatMoney(prorationDueNow, currency)}
                  </Typography>
                </Box>
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: 'block' }}
              >
                {dueCopy}
              </Typography>
            </Box>

            {/* New monthly */}
            <Box
              sx={{
                flex: 1,
                p: 1.75,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <AutorenewRoundedIcon fontSize="small" />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={800}
                  >
                    {recurringLabel}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 950, lineHeight: 1.1 }}
                  >
                    {formatMoney(newMonthly, currency)}
                  </Typography>
                </Box>
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: 'block' }}
              >
                {recurringCaption}
              </Typography>
            </Box>
          </Stack>

          {/* Breakdown toggle */}
          {breakdown ? (
            <>
              <Divider />
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2" fontWeight={900}>
                  Breakdown
                </Typography>

                <Button
                  size="small"
                  onClick={() => setShowBreakdown((p) => !p)}
                  endIcon={
                    <IconButton
                      size="small"
                      sx={{
                        p: 0.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        transform: showBreakdown
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 180ms ease',
                      }}
                    >
                      <ExpandMoreRoundedIcon fontSize="small" />
                    </IconButton>
                  }
                  sx={{ textTransform: 'none', fontWeight: 900, px: 0 }}
                >
                  {showBreakdown ? 'Hide' : 'Show'}
                </Button>
              </Stack>

              <Collapse in={showBreakdown}>
                <Stack spacing={1.25} sx={{ mt: 1 }}>
                  {/* Proration section */}
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      bgcolor: 'action.hover',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="baseline"
                    >
                      <Typography
                        variant="caption"
                        fontWeight={900}
                        color="text.secondary"
                      >
                        Proration (today)
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={900}
                        color="text.secondary"
                      >
                        {formatMoney(breakdown.totalNow, currency)}
                      </Typography>
                    </Stack>

                    <Stack spacing={0.75} sx={{ mt: 1 }}>
                      {breakdown.now.map((l, idx) => (
                        <Stack
                          key={`now-${idx}`}
                          direction="row"
                          justifyContent="space-between"
                          spacing={2}
                        >
                          <Typography
                            variant="body2"
                            sx={{ flex: 1 }}
                            color="text.secondary"
                          >
                            {l.desc}
                          </Typography>
                          <Typography variant="body2" fontWeight={800}>
                            {formatMoney(l.amount, currency)}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>

                  {/* Recurring section */}
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      bgcolor: 'action.hover',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="baseline"
                    >
                      <Typography
                        variant="caption"
                        fontWeight={900}
                        color="text.secondary"
                      >
                        Recurring (monthly)
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={900}
                        color="text.secondary"
                      >
                        {formatMoney(breakdown.totalRecurring, currency)}
                      </Typography>
                    </Stack>

                    <Stack spacing={0.75} sx={{ mt: 1 }}>
                      {breakdown.recurring.map((l, idx) => (
                        <Stack
                          key={`rec-${idx}`}
                          direction="row"
                          justifyContent="space-between"
                          spacing={2}
                        >
                          <Typography
                            variant="body2"
                            sx={{ flex: 1 }}
                            color="text.secondary"
                          >
                            {l.desc}
                          </Typography>
                          <Typography variant="body2" fontWeight={800}>
                            {formatMoney(l.amount, currency)}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    Totals are estimates and may vary slightly due to taxes,
                    discounts, or timing.
                  </Typography>
                </Stack>
              </Collapse>
            </>
          ) : null}

          {/* CTA row */}
          {(onConfirm || onCancel) && (
            <>
              <Divider />
              <Stack
                direction={{ xs: 'column-reverse', sm: 'row' }}
                spacing={1}
                justifyContent="flex-end"
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                {onCancel ? (
                  <Button
                    variant="outlined"
                    onClick={onCancel}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 900,
                      borderRadius: 2.25,
                      py: 1,
                    }}
                    disabled={loading}
                  >
                    {cancelLabel}
                  </Button>
                ) : null}

                {onConfirm ? (
                  <Button
                    variant="contained"
                    onClick={onConfirm}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 950,
                      borderRadius: 2.25,
                      py: 1,
                      px: 2.25,
                      bgcolor: accent,
                      color: '#fff',
                      '&:hover': {
                        bgcolor: accent,
                        filter: 'brightness(0.97)',
                      },
                    }}
                    disabled={loading}
                  >
                    {confirmLabel}
                  </Button>
                ) : null}
              </Stack>
            </>
          )}
        </Stack>
      </Box>
    </Paper>
  )
}
